<script setup lang="ts" name="OrganisationHomeAgency">
import { computed, ref } from 'vue';

import { getOrgRoute } from '~/features/useRouteHelpers';
import { useService } from '~/features/useService';

import Service from '~/services/Service';

import type {
  IDataTableSettings,
  IHomeResponse,
  ILatestNews,
  IOrganisationHomeAgencyNominationsRow,
  IQuickLink,
  IVesselLocation,
  IVesselVoyageData,
} from '~/types';

import BaseButton from './BaseButton.vue';
import BaseCard from './BaseCard.vue';
import BaseCardLink from './BaseCardLink.vue';
import BaseCardServerError from './BaseCardServerError.vue';
import BasePage from './BasePage.vue';
import ComponentErrorCard from './ComponentErrorCard.vue';
import DataTable from './DataTable.vue';
import DataTableLoading from './DataTableLoading.vue';
import OrganisationHomeTheLatestNews from './OrganisationHomeTheLatestNews.vue';
import ServiceCard from './ServiceCard.vue';
import VesselMap from './VesselMap.vue';

const { organisationId } = defineProps<{
  page: { title: string };
  organisationId: OrganisationId;
}>();

const tableSettings: IDataTableSettings<IOrganisationHomeAgencyNominationsRow> =
  {
    editable: false,
    columns: [
      { header: 'Port', key: 'name' },
      {
        header: 'New Nominations',
        key: 'newCount',
        align: 'right',
        fallback: 0,
      },
      {
        header: 'Ongoing Nominations',
        key: 'ongoingCount',
        align: 'right',
        fallback: 0,
      },
      {
        header: 'Total',
        key: 'totalCount',
        align: 'right',
        fallback: 0,
      },
    ],
  };

const quickLinks: IQuickLink[] = [
  {
    title: 'Voyages',
    label: 'Go to Port Calls',
    to: getOrgRoute('OrganisationPortCallsIndex', organisationId),
  },
  {
    title: 'TMS',
    label: 'Go to TMS',
    href: 'https://www.seatechecds.com/edoc/?App=TMS',
    target: '_blank',
  },
  {
    title: 'Ocean Docs',
    label: 'Go to OceanDocs',
    href: 'https://oceandocs.systems/',
    target: '_blank',
  },
];

const tableRows = ref<IOrganisationHomeAgencyNominationsRow[]>([]);
const vesselMapData = ref<IVesselVoyageData>({} as IVesselVoyageData);
const latestNews = ref<ILatestNews>();
const isPageError = computed(
  () => asideServiceState.stateIsError && widgetServiceState.stateIsError,
);

const widgetServiceState = useService(
  Service.organisation(organisationId).portCallsCalls().totals(),
  ({ data }: { data: IOrganisationHomeAgencyNominationsRow[] }) => {
    if (!data.length) return false;
    tableRows.value = data;

    return true;
  },
);

const mapServiceState = useService(
  Service.organisation(organisationId).portCallsCalls().vessels(),
  ({ data }: { data: IVesselLocation[] }) => {
    vesselMapData.value = { vessels: data };

    return true;
  },
);

const asideServiceState = useService(
  Service.organisation(organisationId).home().show(),
  (response: IHomeResponse) => {
    latestNews.value = response.data.latestNews;
    return true;
  },
);

const refreshPage = () => {
  widgetServiceState.fetchData();
  asideServiceState.fetchData();
};
</script>

<template>
  <BasePage v-bind="page" with-aside>
    <div v-if="isPageError" class="col-span-full">
      <BaseCardServerError styled @retry="refreshPage" />
    </div>

    <div class="col-span-full space-y-2" v-else>
      <ServiceCard v-bind="widgetServiceState" styled>
        <template #loading>
          <BaseCard title="Nominations">
            <DataTableLoading />
          </BaseCard>
        </template>

        <BaseCard title="Nominations">
          <DataTable :data="tableRows" :settings="tableSettings"></DataTable>
          <div
            class="-mx-6 flex justify-center border-t border-t-charcoal-2 px-6 pt-6"
          >
            <BaseButton
              variant="btn-tertiary"
              :to="getOrgRoute('OrganisationPortCallsIndex', organisationId)"
            >
              Go to port calls
            </BaseButton>
          </div>
        </BaseCard>
      </ServiceCard>

      <BaseCard title="Voyage Map">
        <div class="h-[500px]">
          <VesselMap :serviceState="mapServiceState" :mapData="vesselMapData" />
        </div>
      </BaseCard>
    </div>

    <template v-if="!isPageError" #aside>
      <div class="space-y-2">
        <BaseCard title="Quick links">
          <div class="-mt-4 divide-y divide-charcoal-2">
            <div
              v-for="(item, index) in quickLinks"
              :key="index"
              class="space-y-2 py-4"
            >
              <p
                class="text-sm"
                :class="{
                  'text-loading w-32': asideServiceState.stateIsLoading,
                }"
              >
                {{ item.title }}
              </p>
              <BaseCardLink
                :to="item.to"
                :href="item.href"
                :target="item.target"
                :loading="asideServiceState.stateIsLoading"
                :disabled="asideServiceState.stateIsLoading"
                @click="item.click"
              >
                {{ item.label }}
              </BaseCardLink>
            </div>
          </div>
        </BaseCard>

        <ComponentErrorCard
          v-if="asideServiceState.stateIsError"
          spacing="card-sm"
          styled
          @retry="asideServiceState.fetchData"
        />

        <OrganisationHomeTheLatestNews
          v-else
          v-bind="latestNews"
          :loading="asideServiceState.stateIsLoading"
        />
      </div>
    </template>
  </BasePage>
</template>
